// ShortcutSettingsModal — `ShortcutSettingsPanel` を載せる独立モーダル外枠
// (shortcut-settings.md §4-§5、スライス2)。
//
// 役割分担:
//   - 本 component = モーダルの **外枠 chrome** だけ (backdrop / 見出し / × / Esc)。
//     `UserPreferencesModal` の外枠と統一感を持たせる (= 同じ class 構造を流用)。
//   - 中身 = self-contained な `ShortcutSettingsPanel` (= 一覧 + リバインド + 永続化)。
//
// 二段構え (shortcut-settings.md §置き場所): 将来「設定」タブ shell に畳むとき、本
// モーダル外枠を捨てて `ShortcutSettingsPanel` をタブ body に **そのまま再マウント**
// するだけで済む (= パネルが外枠と疎結合なので作り直し不要)。
//
// Esc で閉じる挙動は `UserPreferencesModal` / `RoomSettingsModal` と同パターン。

import { useEffect, useId } from 'react';
import type { ShortcutLayout } from './layouts/shortcutRegistry.js';
import { ShortcutSettingsPanel } from './ShortcutSettingsPanel.js';

export interface ShortcutSettingsModalProps {
  /** 現在のレイアウト (= パネルへ渡してグレーアウト判定に使う)。 */
  readonly currentLayout: ShortcutLayout;
  /** Modal を閉じる。 */
  readonly onClose: () => void;
}

export const ShortcutSettingsModal = ({
  currentLayout,
  onClose,
}: ShortcutSettingsModalProps): React.ReactNode => {
  const titleId = useId();

  // Esc で閉じる (UserPreferencesModal / RoomSettingsModal と同パターン)。
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      {/* Backdrop (UserPreferencesModal と同流儀の a11y compliant button)。 */}
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={onClose}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // w-full max-w + min-w-0: max-w-full だけでは grid 子の intrinsic min が
        // max-w を貫通し得る (2026-07-03 一括点検、AnnounceEntryModal と同パターン)。
        className="max-h-[90vh] w-full min-w-0 max-w-[460px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="font-bold text-[var(--color-accent)] text-base">
            ショートカット設定
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="rounded px-2 py-1 text-[var(--color-text-muted)] text-sm hover:bg-white/10"
          >
            ✕
          </button>
        </div>
        <ShortcutSettingsPanel currentLayout={currentLayout} />
      </div>
    </div>
  );
};
